import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const eventTypeLabels: Record<string, string> = {
  wedding: "Wedding", birthday: "Birthday", anniversary: "Anniversary",
  graduation: "Graduation", memorial: "Memorial", proposal: "Proposal",
  baby_shower: "Baby Shower", retirement: "Retirement", other: "Other",
};

interface Review {
  id: string;
  rating: number;
  review_text: string | null;
  reviewer_name: string | null;
  occasion: string | null;
}

const ReviewsSection = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      const { data } = await supabase
        .from("reviews")
        .select("id, rating, review_text, reviewer_name, occasion")
        .eq("is_featured", true)
        .eq("is_approved", true)
        .order("created_at", { ascending: false })
        .limit(6);

      if (data) setReviews(data);
      setLoading(false);
    };
    fetchReviews();
  }, []);

  // Hide section when no reviews
  if (loading || reviews.length === 0) return null;

  return (
    <section aria-labelledby="reviews-heading" className="py-16 md:py-24 bg-card">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 id="reviews-heading" className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
            What Our Customers Say
          </h2>
          <div className="section-divider mb-6" aria-hidden="true" />
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Real stories from people who turned their moments into songs.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {reviews.map((review, index) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-background rounded-2xl p-6 shadow-soft relative"
            >
              <Quote className="absolute top-4 right-4 w-8 h-8 text-primary/10" aria-hidden="true" />
              
              {/* Stars */}
              <div className="flex gap-1 mb-3" role="img" aria-label={`${review.rating} out of 5 stars`}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    aria-hidden="true"
                    className={`w-4 h-4 ${
                      star <= review.rating
                        ? "fill-primary text-primary"
                        : "text-muted-foreground/20"
                    }`}
                  />
                ))}
              </div>

              {/* Review text */}
              {review.review_text && (
                <p className="text-foreground text-sm leading-relaxed mb-4">
                  "{review.review_text}"
                </p>
              )}

              {/* Author */}
              <div className="border-t border-border pt-3 mt-auto">
                <p className="font-semibold text-foreground text-sm">
                  {review.reviewer_name || "Happy Customer"}
                </p>
                {review.occasion && (
                  <p className="text-muted-foreground text-xs">
                    {eventTypeLabels[review.occasion] || review.occasion}
                  </p>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
